/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import type {
  Editor,
  InputDescription,
  IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import {
  SplitType,
  ValueDescription_T,
} from "@smartsuite/automation-engine-api";
import {
  DropdownPanel,
  MenuOption,
  MenuOptionWrapper,
  Paragraph,
  PickerInputField,
} from "@smartsuite/react-ui/lib";
import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";

import { observer } from "mobx-react-lite";
import type { Ref } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CaptionBlock } from "../../../../../../../../components/CaptionBlock";
import { useTheme } from "../../../../../../../../stores";
import { useFieldMissingValidation } from "../../../../../../../../stores/automation/hooks/field-missing-validation";
import { useRootStore } from "../../../../../../../../stores/hooks";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import type { DynamicSection } from "../../../../../../../../types/line";
import { ActionLabel } from "../../../../../../../../utils/constants";
import { getLocalCssColorVariables } from "../../../../../../../../utils/cssVariablesUtils";
import { FieldMissingError } from "../../../../../../Sections/ErrorSection";
import { POSITION_AT, POSITION_MY } from "../../../../style";
import {
  convertActionsToDynamicSections,
  convertTriggerToDynamicSection,
} from "../../../../utils";
import { StyledContainerWrapper } from "../../../style";
import { ActionOptions } from "../../containers/ActionOptions";
import { TriggerOptions } from "../../containers/TriggerOptions";
import { UpdateValueDropdown } from "../../containers/UpdateValueDropdown";
import { OperatorDescriptionKinds } from "../../containers/UpdateValueDropdown/constanst";
import {
  actionsToOptions,
  calculateConfigValue,
  ifLoopingSourceIsActionFind,
} from "../utils";
import { Root, StyledReactInputFieldContainer } from "./style";
import type { Option } from "./utils";
import {
  useConverters,
  ejectPillFromInput,
  mergeNextPillToInput,
  triggerToOptions,
} from "./utils";
import { SolutionColorVariables } from "@smartsuite/types";
import { CurrentOption } from "../../containers/CurrentOption";
import { Tab } from "../../containers/TriggerDropdown/TriggerDropdown";
import { useLoopingMenu } from "../../../../hooks/use-looping-menu";
import { createLoopItemPill } from "../../../../utils/createLoopItemPill";
import {
  actionSourceBoundary,
  canOfferActionSources,
  resolveEditorLoopScope,
} from "../../../../utils/action-sources";
import type { LoopScope } from "../../../../../../../../stores/automation/looping.store";

export const LinkEditor = observer<{
  config: InputCandidate;
  editor: Editor;
  trigger: TriggerCandidate;
  actions: ActionCandidate[];
  action?: ActionCandidate;
  /** Set by ArgumentEditor when `actions` is an explicit, already-scoped list
   * (Conditional Logic branch conditions): offer it whole even though there is
   * no owning `action` to bound it (BUG-21770). */
  actionsArePreScoped?: boolean;
  /** The loop whose Current Item this editor may offer, used verbatim when
   * `actionsArePreScoped` is set. See useLoopingMenu (BUG-21770). */
  loopScope?: LoopScope;
  onChange: (input: InputCandidate) => void;
  integrations: IntegrationDescription[];
  isMultiple: boolean;
  onClickTrash?: () => void;
  isError?: boolean;
  description?: InputDescription;
  disableSplit?: boolean;
}>(
  ({
    config,
    editor,
    trigger,
    actions,
    action,
    actionsArePreScoped,
    loopScope,
    onChange,
    integrations,
    isMultiple,
    description,
    isError,
    onClickTrash,
    disableSplit,
  }) => {
    const { t } = useTranslation();
    const store = useRootStore();
    const theme = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [tab, setTab] = useState<
      "root" | "trigger-fields" | "action-fields" | "options" | "current"
    >("trigger-fields");
    const [triggerFields, setTriggerFields] = useState<Option[]>([]);
    const [actionsFields, setActionsFields] = useState<Option[]>([]);
    const [triggerMenu, setTriggerMenu] = useState<DynamicSection | null>(null);
    const [actionMenus, setActionMenus] = useState<DynamicSection[]>([]);
    const [selectedActionMenu, setSelectedActionMenu] = useState<
      DynamicSection | undefined
    >();
    const buttonRef = useRef<HTMLButtonElement>();
    const [pills, setPills] = useState<PillNodeAttrs[]>([]);
    const [validationError, setValidationError] = useState<string>("");
    const [focus, setFocus] = useState<boolean>(false);
    const { isInputValidByFieldMissing, getFieldMissingErrorMessage } =
      useFieldMissingValidation();
    const editorWrapperId = `${config.inputId}-editor-wrapper`;
    const [actionType, setActionType] = useState<
      | "replace"
      | "clear"
      | "append"
      | "mathAdd"
      | "mathSubtract"
      | "mathMultiply"
      | "mathDivide"
      | undefined
    >(config.operatorConfig?.operator?.oneofKind);
    const [isCleanAction, setIsCleanAction] = useState<boolean>(false);
    const rootLooping = store.automations.candidate.looping;
    const looping = resolveEditorLoopScope(
      action,
      rootLooping,
      loopScope,
      actionsArePreScoped,
    );

    const { convertInputToLinkPills } = useConverters();

    const { loopingMenu, loopingFields } = useLoopingMenu({
      loopScope,
      actionsArePreScoped,
      trigger,
      actions,
      integrations,
      types: [ValueDescription_T.LINK],
      ssFieldTypes: ["linkfield"],
      systemAllowedOptions: ["context-output-record-url"],
      action,
    });

    useEffect(() => {
      setTriggerMenu(
        convertTriggerToDynamicSection(
          trigger,
          integrations,
          [ValueDescription_T.STRING],
          [ValueDescription_T.LINK, ValueDescription_T.STRING],
          ["linkfield", "textfield", "textareafield"],
          ["context-output-record-url"],
        ),
      );
      setTriggerFields(
        triggerToOptions(
          trigger,
          [ValueDescription_T.STRING],
          [ValueDescription_T.LINK, ValueDescription_T.STRING],
          ["linkfield", "textfield", "textareafield"],
          ["context-output-record-url"],
        ),
      );
    }, [integrations, trigger]);

    useEffect(() => {
      if (canOfferActionSources(action, actionsArePreScoped)) {
        setActionsFields(
          actionsToOptions(
            actions,
            [ValueDescription_T.LINK, ValueDescription_T.STRING],
            ["linkfield", "textfield"],
          ),
        );
        setActionMenus(
          convertActionsToDynamicSections(
            actions,
            actionSourceBoundary(action, actionsArePreScoped),
            integrations,
            [ValueDescription_T.LINK, ValueDescription_T.STRING],
            ["linkfield", "textfield"],
            ["context-output-record-url"],
            looping,
          ),
        );
      }
    }, [actions, action, integrations, actionsArePreScoped, loopScope]);

    useEffect(() => {
      if (config.validationError !== "") {
        setValidationError(config.validationError ?? "");
      } else {
        setValidationError("");
      }
    }, [config]);

    useEffect(() => {
      setPills(
        convertInputToLinkPills(
          config,
          triggerFields,
          actionsFields,
          trigger,
          actions,
          action,
          loopingFields,
        ),
      );
    }, [actionsFields, config, triggerFields, looping]);

    useEffect(() => {
      if (triggerMenu?.options.length === 0) {
        setTab("root");
      } else {
        setTab("trigger-fields");
      }
    }, [triggerMenu]);

    useEffect(() => {
      if (store.inputToFocus?.inputId === config.inputId) {
        setIsOpen(!!isMenuAvaliable);
        const focusTimeoutId = setTimeout(() => {
          const wrapper = document.getElementById(editorWrapperId);
          const editor = wrapper?.querySelector(".picker-input-field__input");
          // @ts-expect-error
          editor?.focus();
          store.inputToFocus = undefined;
        });
        return () => clearTimeout(focusTimeoutId);
      }
    }, []);

    useEffect(() => {
      if (actionType === undefined) return;
      if (actionType === OperatorDescriptionKinds.CLEAR) {
        setIsOpen(false);
        setIsCleanAction(true);
      } else {
        setIsCleanAction(false);
      }
      onChange({
        ...config,
        value:
          actionType === OperatorDescriptionKinds.CLEAR
            ? {
                values: [],
              }
            : config.value,
        operatorConfig: {
          // @ts-expect-error
          operator: {
            oneofKind: actionType,
            [actionType]: {},
          },
        },
      });
    }, [actionType, isOpen]);

    const addPill = (attrs: PillNodeAttrs): void => {
      setTimeout(() => {
        setIsOpen(isMultiple);
      });
      const newPills = isMultiple ? [...pills, attrs] : [attrs];

      setPills(newPills);
      onChange({
        ...config,
        value: calculateConfigValue(newPills, actions, trigger),
      });
    };

    const handleInputAdd = (value: string): void => {
      setIsOpen(false);
      onChange(
        mergeNextPillToInput(config, {
          type: "custom",
          value,
        }),
      );
    };

    const isNextPillAllowed = (config: InputCandidate): boolean => {
      if (config.value?.values.length === 1) {
        if (focus) {
          setFocus(false);
        }
        return false;
      } else {
        return true;
      }
    };

    const changeActionType = (
      type:
        | "replace"
        | "clear"
        | "append"
        | "mathAdd"
        | "mathSubtract"
        | "mathMultiply"
        | "mathDivide"
        | undefined,
    ): void => {
      setActionType(type);
    };

    const triggerOutputs = triggerMenu?.options ?? [];

    const isMenuAvaliable =
      triggerOutputs.length > 0 ||
      actionMenus.some((menu) => menu.options.length > 0);

    const disabledOptionId = pills.map((pill) => pill.title);

    return (
      <StyledContainerWrapper id={editorWrapperId}>
        {description !== undefined && (
          <CaptionBlock
            description={description}
            onClickTrash={onClickTrash}
            splitType={config.splitType ?? SplitType.NONE}
            onSplitChange={
              isMultiple && !disableSplit
                ? (splitType) => {
                    onChange({
                      ...config,
                      splitType,
                    });
                  }
                : undefined
            }
          />
        )}
        <Root>
          <StyledReactInputFieldContainer>
            <PickerInputField
              showAddButton={!isCleanAction}
              className={`${isError ? "is-error" : ""} ${
                isCleanAction ? "is-clear" : ""
              }`}
              onChangeFocused={(isFocused, event) => {
                setFocus(isFocused);

                if (
                  description?.editor?.hidePopupOnHover !== true &&
                  isFocused &&
                  isMenuAvaliable &&
                  event?.target.className !== "field-type-pill__remove"
                ) {
                  setIsOpen(true);
                }
              }}
              onInputChange={() => {
                setIsOpen(false);
              }}
              state={
                validationError !== "" ||
                !isInputValidByFieldMissing(config, action)
                  ? "error"
                  : isOpen || focus
                    ? "active"
                    : "default"
              }
              pickerIcon={isMenuAvaliable ? undefined : ""}
              // @ts-expect-error
              buttonRef={buttonRef as Ref<HTMLButtonElement>}
              isOpen={isOpen}
              pills={pills}
              pillStyle={{
                textSize: "m",
              }}
              onButtonClick={() =>
                setIsOpen((open) => isMenuAvaliable && !open)
              }
              onPillRemove={(index) => {
                onChange(ejectPillFromInput(config, index));
              }}
              allowManualInput={
                isCleanAction
                  ? false
                  : isMultiple
                    ? true
                    : isNextPillAllowed(config)
              }
              onInputAdd={handleInputAdd}
              placeholder={
                isCleanAction
                  ? t("automation-form.clean-operator-editor-placeholder")
                  : isOpen || focus
                    ? pills.length === 0
                      ? t("automation-form.number-editor-placeholder")
                      : ""
                    : ""
              }
            >
              {action?.actionReference?.actionId === ActionLabel.updateRecord &&
                description?.operators?.operators !== undefined &&
                description?.operators?.operators.length > 0 && (
                  <UpdateValueDropdown
                    operatorConfig={config.operatorConfig}
                    operatorDescriptions={description?.operators.operators}
                    onOpenedChange={setFocus}
                    onChange={changeActionType}
                  />
                )}
              {isOpen && (
                <DropdownPanel
                  targetElementRef={buttonRef}
                  isActive={isOpen}
                  at={POSITION_AT}
                  my={POSITION_MY}
                  minWidth={300}
                  arrowBackground="custom"
                  arrowCustomBackground={SolutionColorVariables.sc1}
                  panelLineBackground={SolutionColorVariables.sc1}
                  portalStyles={getLocalCssColorVariables(theme)}
                  onClickOutside={() => setIsOpen(false)}
                  onCloseAnimationEnd={() => setIsOpen(false)}
                >
                  {tab === "root" && (
                    <MenuOptionWrapper>
                      {triggerOutputs.length > 0 && (
                        <MenuOption
                          submenuArrow="right"
                          onClick={() => setTab("trigger-fields")}
                        >
                          {t("automation-form.fields-from-trigger")}
                        </MenuOption>
                      )}

                      {actionMenus.map(
                        (actionMenu) =>
                          actionMenu?.options.length > 0 && (
                            <MenuOption
                              key={actionMenu.hash}
                              submenuArrow="right"
                              onClick={() => {
                                setSelectedActionMenu(actionMenu);
                                setTab("action-fields");
                              }}
                            >
                              {actionMenu.hideNamePrefix === true
                                ? actionMenu.name
                                : t("field-popups.action-popup", {
                                    name: actionMenu.name,
                                  })}
                            </MenuOption>
                          ),
                      )}
                      <CurrentOption
                        loopingMenu={loopingMenu}
                        loopScope={looping}
                        actionsArePreScoped={actionsArePreScoped}
                        addPill={(looping) => {
                          // Case when source for looping is the find action
                          if (
                            ifLoopingSourceIsActionFind(looping) ||
                            loopingMenu?.options?.length
                          ) {
                            setTab("current");
                            return;
                          }
                          const loopPill = createLoopItemPill(looping);
                          if (loopPill) {
                            addPill(loopPill);
                          }
                        }}
                      />
                    </MenuOptionWrapper>
                  )}
                  {tab === "trigger-fields" && (
                    <TriggerOptions
                      searchPlaceholder={t("automation-form.search-fields")}
                      title={t("automation-form.fields-from-trigger")}
                      onNavigateBack={() => setTab("root")}
                      addPill={addPill}
                      options={triggerOutputs}
                      disabledOptionId={disabledOptionId}
                    />
                  )}
                  {tab === "current" && loopingMenu && (
                    <TriggerOptions
                      tab={Tab.LOOPING_FIELDS}
                      searchPlaceholder={"Search Fields"}
                      title={loopingMenu.name}
                      onNavigateBack={() => setTab("root")}
                      addPill={addPill}
                      options={loopingMenu.options}
                      hasSingleMenuItem={false}
                      loopingMenuHash={loopingMenu.hash}
                      disabledOptionId={disabledOptionId}
                      loopingMenu={loopingMenu}
                    />
                  )}
                  {tab === "action-fields" && (
                    <ActionOptions
                      onNavigateBack={() => setTab("root")}
                      addPill={addPill}
                      selectedActionMenu={selectedActionMenu}
                      selectedActionHash={selectedActionMenu?.hash}
                    />
                  )}
                </DropdownPanel>
              )}
            </PickerInputField>
            {validationError !== "" && (
              <Paragraph color={"error"} size={"xs"}>
                {validationError}
              </Paragraph>
            )}
            <FieldMissingError
              message={getFieldMissingErrorMessage(config, action)}
            />
          </StyledReactInputFieldContainer>
        </Root>
      </StyledContainerWrapper>
    );
  },
);
